import axios from "axios";

import {
  REQUEST_DEL_PHYSICAL_DAMAGE_FAIL,
  REQUEST_DEL_PHYSICAL_DAMAGE_SUCCESS,
  REQUEST_DEL_PHYSICAL_DAMAGE,
  REQUEST_DELETE_IMG_FAIL,
  REQUEST_DELETE_IMG_SUCCESS,
  REQUEST_DELETE_IMG,
  REQUEST_HEADERS_FAIL,
  REQUEST_HEADERS_SUCCESS,
  REQUEST_HEADERS,
  REQUEST_IMG_SUCCESS,
  REQUEST_PHYSICAL_DAMAGE_FAIL,
  REQUEST_PHYSICAL_DAMAGE_SUCCESS,
  REQUEST_PHYSICAL_DAMAGE,
  REQUEST_PHYSICAL_DAMAGES_FAIL,
  REQUEST_PHYSICAL_DAMAGES_SUCCESS,
  REQUEST_PHYSICAL_DAMAGES,
  REQUEST_UPLOAD_ANNOTATED_FAIL,
  REQUEST_UPLOAD_ANNOTATED_SUCCESS,
  REQUEST_UPLOAD_ANNOTATED,
  REQUEST_UPLOAD_IMG_FAIL,
  REQUEST_UPLOAD_IMG_SUCCESS,
  REQUEST_UPLOAD_IMG,
  REQUEST_UPLOAD_ESTIMATED_COST_FAIL,
  REQUEST_UPLOAD_ESTIMATED_COST_SUCCESS,
  REQUEST_UPLOAD_ESTIMATED_COST,
  RESET_IMG_UPLOADS,
  UPDATE_IMG_PROGRESS,
  REQUEST_UPLOAD_DESCRIPTION,
  REQUEST_UPLOAD_DESCRIPTION_SUCCESS,
  REQUEST_UPLOAD_DESCRIPTION_FAIL
} from "./actionTypes";

// Config
import {
  getPhysicalDamageImgUrl,
  getPhysicalDamageImgUploadUrl,
  getPhysicalDamageUrl,
  getPhysicalDamagesUrl,
  getPhysicalDamageEstimatedCostsUrl,
  getPhysicalDamageDescriptionsUrl
} from "../../config/apiConfig";

// Utils
import { parseFetchOptions } from "../user/actions";
import { imageTypes, getImageHeaders } from "../../utils/images";
import appendToPath from "../../utils/appendToPath";
import errors from "../../utils/errors";

// Physical Damages (list)
const requestPhysicalDamages = payload => ({
  type: REQUEST_PHYSICAL_DAMAGES,
  payload
});

const requestPhysicalDamagesSuccess = payload => ({
  type: REQUEST_PHYSICAL_DAMAGES_SUCCESS,
  payload
});

const requestPhysicalDamagesFail = payload => ({
  type: REQUEST_PHYSICAL_DAMAGES_FAIL,
  payload
});

export const getPhysicalDamages =
  ({ caseId, onFail }) =>
  async dispatch => {
    dispatch(requestPhysicalDamages({ caseId }));

    dispatch(
      parseFetchOptions(async options => {
        const response = await fetch(getPhysicalDamagesUrl(caseId), {
          method: "GET",
          headers: options.headers
        }).catch(error => {
          if (onFail) onFail();
          dispatch(requestPhysicalDamagesFail(error));
        });

        if (response && response.ok) {
          const data = await response.json();
          dispatch(requestPhysicalDamagesSuccess(data));
        } else {
          if (onFail) onFail();
          dispatch(requestPhysicalDamagesFail(errors.responseError(response)));
        }
      })
    );
  };

// Physical Damage
const requestPhysicalDamage = () => ({ type: REQUEST_PHYSICAL_DAMAGE });

const requestPhysicalDamageSuccess = payload => ({
  type: REQUEST_PHYSICAL_DAMAGE_SUCCESS,
  payload
});

const requestPhysicalDamageFail = payload => ({
  type: REQUEST_PHYSICAL_DAMAGE_FAIL,
  payload
});

export const getPhysicalDamage =
  ({ caseId, physicalDamageId, onFinish }) =>
  async dispatch => {
    dispatch(requestPhysicalDamage());

    dispatch(
      parseFetchOptions(async options => {
        const response = await fetch(
          getPhysicalDamageUrl(caseId, physicalDamageId),
          {
            method: "GET",
            headers: options.headers
          }
        ).catch(error => {
          if (onFinish) onFinish(false);
          dispatch(requestPhysicalDamageFail(error));
        });

        if (response && response.ok) {
          const data = await response.json();
          dispatch(requestPhysicalDamageSuccess(data));
          if (onFinish) onFinish(true);
        } else {
          if (onFinish) onFinish(false);
          dispatch(requestPhysicalDamageFail(errors.responseError(response)));
        }
      })
    );
  };

// Physical Damage Delete
const requestPhysicalDamageDelete = () => ({
  type: REQUEST_DEL_PHYSICAL_DAMAGE
});

const requestPhysicalDamageDeleteSuccess = payload => ({
  type: REQUEST_DEL_PHYSICAL_DAMAGE_SUCCESS,
  payload
});

const requestPhysicalDamageDeleteFail = payload => ({
  type: REQUEST_DEL_PHYSICAL_DAMAGE_FAIL,
  payload
});

export const deletePhysicalDamage =
  ({ caseId, physicalDamageId, onFinish }) =>
  async dispatch => {
    dispatch(requestPhysicalDamageDelete());

    dispatch(
      parseFetchOptions(async options => {
        let networkError = null;
        const response = await fetch(
          getPhysicalDamageUrl(caseId, physicalDamageId),
          {
            method: "DELETE",
            headers: options.headers
          }
        ).catch(error => {
          networkError = error;
          return null;
        });

        if (response && response.ok) {
          dispatch(requestPhysicalDamageDeleteSuccess());
          if (onFinish) onFinish(true);
        } else {
          dispatch(
            requestPhysicalDamageDeleteFail(
              networkError || errors.responseError(response)
            )
          );
          if (onFinish) onFinish(false);
        }
      })
    );
  };

// Image Get
const requestImageSuccess = payload => ({
  type: REQUEST_IMG_SUCCESS,
  payload
});

export const getImage =
  ({ caseId, filename, index, physicalDamageId, forceReplace }) =>
  async dispatch => {
    dispatch(
      parseFetchOptions(async options => {
        const response = await fetch(
          getPhysicalDamageImgUrl(caseId, physicalDamageId, filename),
          {
            method: "GET",
            headers: options.headers
          }
        );

        if (response && response.ok) {
          const image = await response.json();
          dispatch(requestImageSuccess({ index, image, forceReplace }));
        }
      })
    );
  };

// Image Headers
const requestHeaders = payload => ({
  type: REQUEST_HEADERS,
  payload
});

const requestHeadersSuccess = payload => ({
  type: REQUEST_HEADERS_SUCCESS,
  payload
});

const requestHeadersFail = payload => ({
  type: REQUEST_HEADERS_FAIL,
  payload
});

const headerId = "x-ms-meta-iccv";

export const getHeaders =
  ({ resourceURLs, filename, onFail }) =>
  async dispatch => {
    dispatch(requestHeaders(filename));

    // The scale metadata only ever lives on the original uploaded blob
    // (resourceURLs.image) - downloadURLs' processed preview variants never
    // carry it. resourceURLs.image itself can come back null/absent though,
    // in which case there's nothing to fetch.
    const url = resourceURLs && resourceURLs.image;
    if (!url) {
      dispatch(requestHeadersFail({ filename, error: null }));
      return;
    }

    const headers = await getImageHeaders({
      url,
      onFail: error => {
        if (onFail) onFail();
        dispatch(requestHeadersFail({ filename, error }));
      }
    });

    if (!headers) return;

    dispatch(
      requestHeadersSuccess({
        filename,
        data: JSON.parse(headers.get(headerId))
      })
    );
  };

// Image Upload
const requestUploadImg = payload => ({
  type: REQUEST_UPLOAD_IMG,
  payload
});

const requestUploadImgSuccess = payload => ({
  type: REQUEST_UPLOAD_IMG_SUCCESS,
  payload
});

const requestUploadImgFail = payload => ({
  type: REQUEST_UPLOAD_IMG_FAIL,
  payload
});

export const updateImgProgress = payload => ({
  type: UPDATE_IMG_PROGRESS,
  payload
});

export const resetImgUploads = () => ({ type: RESET_IMG_UPLOADS });

export const uploadPhysicalDamageImage =
  ({
    caseId,
    files,
    id,
    physicalDamageId,
    onFail,
    onUploadProgress,
    query = ""
  }) =>
  async dispatch => {
    dispatch(
      parseFetchOptions(async options => {
        const requestResponse = await fetch(
          `${getPhysicalDamageImgUploadUrl(caseId, physicalDamageId)}${query}`,
          {
            method: "POST",
            headers: {
              ...options.headers,
              "Content-Type": "application/json"
            },
            body: JSON.stringify(
              files.map(({ file, description }) => ({
                filename: file.name,
                description,
                imageType: imageTypes.noGrid
              }))
            )
          }
        );

        if (requestResponse && requestResponse.ok) {
          const { uploadURL, closeSessionURL } = await requestResponse.json();

          files.forEach(({ file }, i) => {
            dispatch(requestUploadImg(i));

            axios
              .request({
                method: "PUT",
                headers: {
                  "x-ms-blob-type": "BlockBlob",
                  "Content-Type": file.type
                },
                data: file,
                url: appendToPath(uploadURL, file.name),
                onUploadProgress: onUploadProgress(i)
              })
              .then(async () => {
                await fetch(closeSessionURL).catch(() =>
                  onFail("Failed to close img upload session")
                );
                dispatch(requestUploadImgSuccess(id));
              })
              .catch(() => {
                if (onFail) onFail("Failed to upload image");
                dispatch(requestUploadImgFail(id));
              });
          });
        } else onFail("Failed to get img upload url");
      })
    );
  };

export const retryUploadPhysicalDamageImage =
  ({ id, image, onFail, onSuccess, url }) =>
  async dispatch => {
    dispatch(requestUploadImg(id));
    axios
      .request({
        method: "PUT",
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": image.type
        },
        data: image,
        url,
        onUploadProgress: progress =>
          dispatch(updateImgProgress({ index: 0, progress }))
      })
      .then(async () => {
        dispatch(requestUploadImgSuccess(id));
        if (onSuccess) onSuccess();
      })
      .catch(() => {
        if (onFail) onFail();
        dispatch(requestUploadImgFail(id));
      });
  };

// Annotated Image Upload
const requestUploadAnnotated = () => ({ type: REQUEST_UPLOAD_ANNOTATED });

const requestUploadAnnotatedSuccess = payload => ({
  type: REQUEST_UPLOAD_ANNOTATED_SUCCESS,
  payload
});

const requestUploadAnnotatedFail = payload => ({
  type: REQUEST_UPLOAD_ANNOTATED_FAIL,
  payload
});

export const uploadAnnotatedImage =
  ({
    caseId,
    description,
    filename,
    image,
    imageMeta,
    physicalDamageId,
    onFail,
    onSuccess
  }) =>
  async dispatch => {
    dispatch(requestUploadAnnotated());

    dispatch(
      parseFetchOptions(async options => {
        const requestResponse = await fetch(
          getPhysicalDamageImgUploadUrl(caseId, physicalDamageId),
          {
            method: "POST",
            headers: {
              ...options.headers,
              "Content-Type": "application/json"
            },
            body: JSON.stringify([
              {
                filename,
                description,
                imageType: imageTypes.annotated
              }
            ])
          }
        ).catch(error => {
          if (onFail) onFail(`Failed to get annotated img upload url`);
          dispatch(requestUploadAnnotatedFail(error));
        });

        if (requestResponse && requestResponse.status === 200) {
          const requestBody = await requestResponse.json();

          const uploadResponse = await fetch(
            appendToPath(requestBody.uploadURL, filename),
            {
              method: "PUT",
              headers: {
                "x-ms-blob-type": "BlockBlob",
                "Content-Type": "image/jpeg",
                "x-ms-meta-iccv": JSON.stringify(imageMeta)
              },
              body: image
            }
          ).catch(error => {
            if (onFail) onFail(`Failed to upload annotated img`);
            dispatch(requestUploadAnnotatedFail(error));
          });

          await fetch(requestBody.closeSessionURL);

          if (uploadResponse && uploadResponse.status === 201) {
            dispatch(requestUploadAnnotatedSuccess());
            dispatch(getImage({ caseId, filename, physicalDamageId }));
            if (onSuccess) onSuccess();
          } else {
            if (onFail) onFail(`Failed to close annotated img upload session`);
            dispatch(
              requestUploadAnnotatedFail(
                new Error(`uploadResponse: ${JSON.stringify(uploadResponse)}`)
              )
            );
          }
        } else {
          if (onFail) onFail(`Failed to get annotated img upload url`);
          dispatch(
            requestUploadAnnotatedFail(
              new Error(`requestResponse: ${JSON.stringify(requestResponse)}`)
            )
          );
        }
      })
    );
  };

// Image Delete
const requestDeleteImg = payload => ({ type: REQUEST_DELETE_IMG, payload });

const requestDeleteImgSuccess = payload => ({
  type: REQUEST_DELETE_IMG_SUCCESS,
  payload
});

const requestDeleteImgFail = payload => ({
  type: REQUEST_DELETE_IMG_FAIL,
  payload
});

export const deleteImage =
  ({ caseId, physicalDamageId, filename, onSuccess, onFail }) =>
  async dispatch => {
    dispatch(requestDeleteImg(filename));

    dispatch(
      parseFetchOptions(async options => {
        const response = await fetch(
          getPhysicalDamageImgUrl(caseId, physicalDamageId, filename),
          {
            method: "DELETE",
            headers: options.headers
          }
        ).catch(error => {
          if (onFail) onFail();
          dispatch(requestDeleteImgFail(error));
        });

        if (response && response.ok) {
          dispatch(requestDeleteImgSuccess(filename));
          if (onSuccess) onSuccess();
        } else {
          if (onFail) onFail();
          dispatch(requestDeleteImgFail(errors.responseError(response)));
        }
      })
    );
  };

// Estimated Costs
const uploadEstimatedCostRequest = () => ({
  type: REQUEST_UPLOAD_ESTIMATED_COST
});
const uploadEstimatedCostSuccess = () => ({
  type: REQUEST_UPLOAD_ESTIMATED_COST_SUCCESS
});

const uploadEstimatedCostFail = error => ({
  type: REQUEST_UPLOAD_ESTIMATED_COST_FAIL,
  payload: error
});

export const uploadEstimatedCost =
  ({ caseId, physicalDamageId, texts, onFinish }) =>
  async dispatch => {
    dispatch(uploadEstimatedCostRequest());

    dispatch(
      parseFetchOptions(async options => {
        const response = await fetch(
          getPhysicalDamageEstimatedCostsUrl(caseId, physicalDamageId),
          {
            method: "POST",
            headers: {
              ...options.headers,
              "Content-Type": "application/json"
            },
            body: JSON.stringify(texts)
          }
        ).catch(error => {
          dispatch(uploadEstimatedCostFail(errors.responseError(error)));
          if (onFinish) onFinish(false);
        });

        if (response && response.ok) {
          const data = await response.json();
          dispatch(uploadEstimatedCostSuccess());
          dispatch(requestPhysicalDamageSuccess(data));
          if (onFinish) onFinish(true);
        } else {
          dispatch(uploadEstimatedCostFail(errors.responseError(response)));
          if (onFinish) onFinish(false);
        }
      })
    );
  };

// Descriptions
const uploadDescriptionsRequest = () => ({
  type: REQUEST_UPLOAD_DESCRIPTION
});
const uploadDescriptionsSuccess = () => ({
  type: REQUEST_UPLOAD_DESCRIPTION_SUCCESS
});

const uploadDescriptionsFail = error => ({
  type: REQUEST_UPLOAD_DESCRIPTION_FAIL,
  payload: error
});

export const uploadDescriptions =
  ({ caseId, physicalDamageId, texts, onFinish }) =>
  async dispatch => {
    dispatch(uploadDescriptionsRequest());
    dispatch(
      parseFetchOptions(async options => {
        const response = await fetch(
          getPhysicalDamageDescriptionsUrl(caseId, physicalDamageId),
          {
            method: "POST",
            headers: {
              ...options.headers,
              "Content-Type": "application/json"
            },
            body: JSON.stringify(texts)
          }
        ).catch(error => {
          dispatch(uploadDescriptionsFail(errors.responseError(error)));
          if (onFinish) onFinish(false);
        });

        if (response && response.ok) {
          const data = await response.json();
          dispatch(uploadDescriptionsSuccess());
          dispatch(requestPhysicalDamageSuccess(data));
          if (onFinish) onFinish(true);
        } else {
          dispatch(uploadDescriptionsFail(errors.responseError(response)));
          if (onFinish) onFinish(false);
        }
      })
    );
  };

// Update Physical Damage
export const updatePhysicalDamage =
  ({ caseId, physicalDamageId, form, onFinish }) =>
  async dispatch => {
    dispatch(uploadEstimatedCostRequest());
    dispatch(
      parseFetchOptions(async options => {
        const response = await fetch(
          getPhysicalDamageUrl(caseId, physicalDamageId),
          {
            method: "PUT",
            headers: {
              ...options.headers,
              "Content-Type": "application/json"
            },
            // Backend rejects this PUT with a 400 ("The Id field is
            // required") without an id in the body, despite it already
            // being in the URL - matches the same requirement
            // updateInjury's PUT already works around (see injuries/actions.js).
            body: JSON.stringify({ ...form, id: physicalDamageId })
          }
        ).catch(error => {
          dispatch(uploadEstimatedCostFail(errors.responseError(error)));
          if (onFinish) onFinish(false);
        });

        if (response && response.ok) {
          const data = await response.json();
          dispatch(uploadEstimatedCostSuccess());
          dispatch(requestPhysicalDamageSuccess(data));
          if (onFinish) onFinish(true);
        } else {
          dispatch(uploadEstimatedCostFail(errors.responseError(response)));
          if (onFinish) onFinish(false);
        }
      })
    );
  };
